/**
 * Real-time notifications: WebSocket + polling fallback.
 *
 * Data flow:
 * - Primary: WebSocket delivers new notifications and count updates in real time.
 * - Fallback: When WebSocket is disconnected (e.g. iOS PWA after background, or
 *   network blip), we poll the unread-count API so the bell still updates without
 *   page refresh. This satisfies the requirement that iOS PWA users see updates
 *   without pull-to-refresh or reopening the app.
 * - On (re)connect we sync unread count from the API once so UI is correct.
 *
 * Separation of concerns: This hook is the single data source for notification
 * state (unread count, live notifications). UI (NotificationBell) only reads
 * and displays; it does not fetch or decide when to refetch.
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { notificationWebSocket, NotificationMessage } from '../lib/websocket';
import { browserNotificationService, shouldShowNotificationPermissionModal } from '../lib/browserNotifications';
import { queryKeys, getUnreadNotificationCount } from '../lib/api';
import { useNavigate } from 'react-router-dom';

export interface RealtimeNotification {
  id: number;
  type: string;
  actor?: {
    id?: number;
    name?: string;
    username?: string;
    image?: string;
    verified?: boolean;
  };
  post?: {
    id: number;
    hashCode?: string;
    caption?: string;
    image?: string;
    video?: string;
  };
  comment?: {
    id: number;
    content?: string;
    postId?: number;
  };
  read: boolean;
  createdAt: string;
}

export interface UseRealtimeNotificationsOptions {
  onNotification?: (notification: RealtimeNotification) => void;
  showBrowserNotification?: boolean;
  showToast?: boolean;
}

/** Poll interval when WebSocket is disconnected (e.g. iOS PWA after resume). */
const FALLBACK_POLL_INTERVAL_MS = 20_000;
/** How often we check connection state so UI can switch between WS and polling. */
const CONNECTION_CHECK_MS = 2000;

export const useRealtimeNotifications = (options: UseRealtimeNotificationsOptions = {}) => {
  const {
    onNotification,
    showBrowserNotification = true,
    showToast = true,
  } = options;

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [toastNotification, setToastNotification] = useState<RealtimeNotification | null>(null);
  const [unreadCount, setUnreadCount] = useState<number | undefined>(undefined);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  /** Reactive connection state so we can run polling fallback when disconnected. */
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected'>(() => notificationWebSocket.getState());
  const didSyncOnConnect = useRef(false);

  // Initial unread count from API (single load; then WS or fallback poll keep it updated)
  const { data: initialCountData } = useQuery({
    queryKey: queryKeys.unreadNotificationCount,
    queryFn: getUnreadNotificationCount,
    staleTime: 60 * 1000,
    refetchInterval: false,
    refetchOnMount: false, // avoid duplicate request when Strict Mode or auth remounts the bell
  });

  useEffect(() => {
    if (unreadCount === undefined && initialCountData?.count !== undefined) {
      setUnreadCount(initialCountData.count);
    }
  }, [initialCountData, unreadCount]);

  const getNotificationText = useCallback((notification: RealtimeNotification): string => {
    const actorName = notification.actor?.name || notification.actor?.username || 'کاربر';
    switch (notification.type) {
      case 'post_liked':
        return `${actorName} پست شما را لایک کرد`;
      case 'comment':
        return `${actorName} روی پست شما کامنت گذاشت`;
      case 'follow':
        return `${actorName} شما را دنبال کرد`;
      default:
        return 'اعلان جدید';
    }
  }, []);

  const applyUnreadCount = useCallback((count: number) => {
    setUnreadCount(count);
    queryClient.setQueryData(queryKeys.unreadNotificationCount, { count });
  }, [queryClient]);

  const handleMessage = useCallback((message: NotificationMessage) => {
    if (message.type === 'notification' && message.notification) {
      const notification: RealtimeNotification = {
        id: message.notification.id,
        type: message.notification.type,
        actor: message.notification.actor,
        post: message.notification.post,
        comment: message.notification.comment,
        read: message.notification.read,
        createdAt: message.notification.createdAt,
      };

      if (message.count !== undefined) {
        applyUnreadCount(message.count);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications });

      if (showToast) {
        setToastNotification(notification);
      }

      if (showBrowserNotification && browserNotificationService.hasPermission()) {
        browserNotificationService.show({
          title: 'اعلان جدید',
          body: getNotificationText(notification),
          icon: notification.actor?.image || '/favicon.ico',
          tag: `notification-${notification.id}`,
          data: { notificationId: notification.id, postId: notification.post?.id },
          requireInteraction: false,
        }).then((browserNotif) => {
          if (browserNotif) {
            browserNotif.onclick = () => {
              window.focus();
              if (notification.post?.hashCode) {
                navigate(`/p/${notification.post.hashCode}`);
              } else if (notification.post?.id != null) {
                navigate(`/post/${notification.post.id}`);
              }
              browserNotif.close();
            };
          }
        });
      }

      if (onNotification) {
        onNotification(notification);
      }
    } else if (message.type === 'count_update' && message.count !== undefined) {
      applyUnreadCount(message.count);
    }
  }, [queryClient, navigate, getNotificationText, onNotification, showBrowserNotification, showToast, applyUnreadCount]);

  // Connect and subscribe to WebSocket
  useEffect(() => {
    let cancelled = false;
    const connect = async () => {
      try {
        await notificationWebSocket.connect();
        if (cancelled) return;
        setConnectionState('connected');
        didSyncOnConnect.current = false;
      } catch (error) {
        if (!cancelled) {
          setConnectionState('disconnected');
        }
      }
    };
    connect();
    const unsubscribe = notificationWebSocket.onMessage(handleMessage);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [handleMessage]);

  // Keep connection state in sync (so we can show fallback polling when disconnected)
  useEffect(() => {
    const interval = setInterval(() => {
      const state = notificationWebSocket.getState();
      setConnectionState((prev) => {
        if (prev === 'connected' && state === 'disconnected') {
          didSyncOnConnect.current = false;
        }
        return state;
      });
    }, CONNECTION_CHECK_MS);
    return () => clearInterval(interval);
  }, []);

  // When connected: sync unread count from API once so we're correct after reconnect (e.g. iOS PWA resume)
  useEffect(() => {
    if (connectionState !== 'connected') return;
    if (didSyncOnConnect.current) return;
    didSyncOnConnect.current = true;
    getUnreadNotificationCount()
      .then((data) => {
        if (data?.count !== undefined) {
          applyUnreadCount(data.count);
        }
      })
      .catch(() => {});
  }, [connectionState, applyUnreadCount]);

  // Fallback: when WebSocket is disconnected, poll unread count so bell updates without refresh (iOS PWA, network blips)
  useEffect(() => {
    if (connectionState !== 'disconnected') return;
    const poll = () => {
      getUnreadNotificationCount()
        .then((data) => {
          if (data?.count !== undefined) {
            applyUnreadCount(data.count);
          }
        })
        .catch(() => {});
    };
    poll();
    const interval = setInterval(poll, FALLBACK_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [connectionState, applyUnreadCount]);

  useEffect(() => {
    if (showBrowserNotification && browserNotificationService.isSupported()) {
      const permission = browserNotificationService.getPermission();
      if (permission === 'default' && shouldShowNotificationPermissionModal()) {
        const timer = setTimeout(() => setShowPermissionModal(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [showBrowserNotification]);

  const requestPermissionFromUser = useCallback(async () => {
    const permission = await browserNotificationService.requestPermission();
    setShowPermissionModal(false);
    return permission;
  }, []);

  return {
    toastNotification,
    setToastNotification,
    unreadCount,
    setUnreadCount,
    showPermissionModal,
    setShowPermissionModal,
    requestPermissionFromUser,
    isConnected: connectionState === 'connected',
    connectionState,
  };
};
